/* iter486 — Mind & Body Lab page (Phase 1 MVP).
 *
 * Structure mirrors NutritionLabPage:
 *   - Left column: profile builder (conditions / symptoms / goals + categories filter)
 *   - Right column: ranked interventions with IMS badges + detail modal
 *
 * Cross-links:
 *   - "Synergy with Nutrition Lab" tile shows the user's pinned foods
 *   - "Track in Health Intelligence" button on each intervention card
 */
import MedModuleNotes from "@/components/meds/MedModuleNotes";
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import {
  Brain, Dumbbell, Sparkles, Moon, Heart, Loader2, Filter, X, ChevronRight,
  Activity, Salad, ExternalLink, AlertTriangle, ShieldCheck, ArrowUp, ArrowDown,
} from "lucide-react";

// iter487 — Phase 2 + 3 advanced components (weekly plan, synergy, AI coach, correlations)
import { WeeklyPlanCard, SynergyScoreCard, AiCoachChat, CorrelationsCard } from "@/components/mindbody/MindBodyAdvanced";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import CortisolBreathTimer from "@/components/stress/CortisolBreathTimer";
import HowToStartCard from "@/components/HowToStartCard";  // iter503 — guided intro
import LoadFromVaultBanner from "@/components/LoadFromVaultBanner";  // iter906 — Pathway Vault as source of truth
import { Search } from "lucide-react";  // iter503 — search icon for conditions

const CATEGORY_ICONS = {
  exercise:  Dumbbell,
  mind_body: Sparkles,
  sleep:     Moon,
  recovery:  Heart,
  stress:    Brain,
};

const CATEGORY_LABELS = {
  bg: {
    exercise:  "Exercise Lab",
    mind_body: "Mind-Body Lab",
    sleep:     "Sleep Lab",
    recovery:  "Recovery Lab",
    stress:    "Stress Lab",
  },
  en: {
    exercise:  "Exercise Lab",
    mind_body: "Mind-Body Lab",
    sleep:     "Sleep Lab",
    recovery:  "Recovery Lab",
    stress:    "Stress Lab",
  },
  es: {
    exercise:  "Laboratorio de Ejercicio",
    mind_body: "Laboratorio Mente-Cuerpo",
    sleep:     "Laboratorio del Sueño",
    recovery:  "Laboratorio de Recuperación",
    stress:    "Laboratorio del Estrés",
  },
};

const CATEGORY_COLORS = {
  exercise:  "from-emerald-500 to-emerald-600",
  mind_body: "from-violet-500 to-violet-600",
  sleep:     "from-indigo-500 to-indigo-700",
  recovery:  "from-amber-500 to-amber-600",
  stress:    "from-pink-500 to-pink-600",
};

const EVIDENCE_LABEL = {
  A: { bg: "Level A · Meta-анализи / Guidelines", en: "Level A · Meta-analyses / Guidelines", color: "bg-emerald-500" },
  B: { bg: "Level B · Множество RCT",              en: "Level B · Multiple RCTs",              color: "bg-cyan-500" },
  C: { bg: "Level C · Обсервационни",              en: "Level C · Observational",              color: "bg-amber-500" },
  D: { bg: "Level D · Възникваща наука",            en: "Level D · Emerging",                   color: "bg-slate-500" },
};

const DISCLAIMER_KEY = "nutramilo:mindbody:disclaimer:v1";

function imsColor(ims) {
  if (ims >= 80) return "from-emerald-500 to-emerald-600";
  if (ims >= 60) return "from-cyan-500 to-cyan-600";
  if (ims >= 40) return "from-amber-500 to-amber-600";
  return "from-slate-400 to-slate-500";
}

// ─────────────────────────────────────────────────────────────────────────────
// DisclaimerModal — first-visit educational disclaimer (lightweight)
// ─────────────────────────────────────────────────────────────────────────────
function DisclaimerModal({ open, onAccept, onCancel, lang, isDark }) {
  const [agreed, setAgreed] = useState(false);
  if (!open) return null;
  const t = lang === "bg" ? {
    title: "Mind & Body Lab — Educational lifestyle tool",
    intro: "Mind & Body Lab е инструмент за lifestyle medicine. Препоръките са образователни и базирани на peer-reviewed литература, но НЕ заместват лекар, физиотерапевт или психолог.",
    bullets: [
      "Не предписва доза за медикаменти",
      "Не поставя медицинска диагноза",
      "При остра болка/симптом — потърси лекар",
    ],
    consent: "Разбирам, че Mind & Body Lab е educational tool и не замества медицинска консултация.",
    accept:  "Разбрах — продължи",
    cancel:  "Назад",
  } : {
    title: "Mind & Body Lab — Educational lifestyle tool",
    intro: "Mind & Body Lab is a lifestyle medicine tool. Recommendations are educational and peer-reviewed, but do NOT replace your doctor, physiotherapist or psychologist.",
    bullets: [
      "Does not prescribe medication doses",
      "Does not make medical diagnoses",
      "For acute pain/symptoms — seek a doctor",
    ],
    consent: "I understand Mind & Body Lab is an educational tool and does not replace medical consultation.",
    accept:  "Got it — continue",
    cancel:  "Back",
  };
  const accept = () => {
    if (!agreed) return;
    try { localStorage.setItem(DISCLAIMER_KEY, "accepted_v1"); } catch {}
    onAccept?.();
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         data-testid="mindbody-disclaimer-modal" role="dialog" aria-modal="true">
      <div className={`relative w-full max-w-xl rounded-2xl border shadow-2xl ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"} overflow-hidden`}>
        <div className="flex items-start gap-3 p-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-pink-500 shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{t.title}</h2>
            <p className={`text-xs mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>EU AI Act · Lifestyle Medicine</p>
          </div>
        </div>
        <div className="p-6 pt-4 space-y-4">
          <p className={`text-sm ${isDark ? "text-slate-200" : "text-slate-700"}`}>{t.intro}</p>
          <ul className={`space-y-1.5 text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {t.bullets.map((b, i) => <li key={i} className="flex gap-2"><span className="text-rose-500 shrink-0">✕</span><span>{b}</span></li>)}
          </ul>
          <label className="flex items-start gap-3 cursor-pointer pt-2">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                   data-testid="mindbody-disclaimer-checkbox" className="mt-1 w-4 h-4 accent-violet-600" />
            <span className={`text-sm ${isDark ? "text-slate-100" : "text-slate-900"}`}>{t.consent}</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button onClick={onCancel} data-testid="mindbody-disclaimer-cancel"
                    className={`px-4 py-2 rounded-lg text-sm font-medium border ${isDark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
              {t.cancel}
            </button>
            <button onClick={accept} disabled={!agreed} data-testid="mindbody-disclaimer-accept"
                    className={`px-5 py-2 rounded-lg text-sm font-bold text-white ${agreed ? "bg-gradient-to-r from-violet-600 to-pink-600 hover:opacity-90" : "bg-slate-400 opacity-60 cursor-not-allowed"}`}>
              {t.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail Modal
// ─────────────────────────────────────────────────────────────────────────────
function InterventionDetailModal({ data, lang, isDark, onClose, onTrack, tracking }) {
  if (!data) return null;
  const bg = lang === "bg";
  const ev = EVIDENCE_LABEL[data.evidence_level || "C"];
  const CatIcon = CATEGORY_ICONS[data.category] || Sparkles;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
         data-testid="mindbody-detail-modal" role="dialog" aria-modal="true">
      <div className={`relative w-full max-w-2xl rounded-2xl border shadow-2xl ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200"} max-h-[90vh] overflow-y-auto`}>
        <div className={`sticky top-0 z-10 flex items-start gap-3 p-5 pb-4 border-b ${isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${CATEGORY_COLORS[data.category]} shrink-0`}>
            <CatIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? data.name_bg : data.name_en}
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {CATEGORY_LABELS[bg ? "bg" : "en"][data.category]} · {bg ? "Прозорец" : "Window"}: {data.expected_window_weeks} {bg ? "седмици" : "weeks"}
            </p>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg ${isDark ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-slate-100"}`}
                  aria-label="Close" data-testid="mindbody-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 pt-4 space-y-4">
          {/* Evidence + Dose */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={`rounded-xl border p-3 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"} mb-1`}>
                {bg ? "Сила на доказателства" : "Evidence level"}
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black text-white ${ev.color}`}>{data.evidence_level}</span>
                <span className={`text-xs ${isDark ? "text-slate-200" : "text-slate-700"}`}>{ev[bg ? "bg" : "en"]}</span>
              </div>
            </div>
            <div className={`rounded-xl border p-3 ${isDark ? "border-slate-700 bg-slate-800/50" : "border-slate-200 bg-slate-50"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-slate-400" : "text-slate-500"} mb-1`}>
                {bg ? "Препоръчителна доза" : "Recommended dose"}
              </div>
              <div className={`text-xs ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                {bg ? data.dose_bg : data.dose_en}
              </div>
            </div>
          </div>

          {/* Mechanisms */}
          {(data.mechanisms || []).length > 0 && (
            <section data-testid="mindbody-mechanisms">
              <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {bg ? "Биологични механизми" : "Biological mechanisms"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {data.mechanisms.map((m, i) => {
                  const up = m.direction === "up";
                  return (
                    <div key={i} className={`flex items-center gap-2 rounded-lg border p-2.5 ${isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-white"}`}>
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${up ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"}`}>
                        {up ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-semibold truncate ${isDark ? "text-slate-100" : "text-slate-900"}`}>{bg ? m.marker : m.marker_en}</div>
                        <div className="flex gap-0.5 mt-0.5">
                          {[1, 2, 3].map((s) => (
                            <span key={s} className={`h-1 w-3 rounded-full ${s <= (m.strength || 1) ? (up ? "bg-emerald-500" : "bg-rose-500") : (isDark ? "bg-slate-700" : "bg-slate-200")}`}></span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Supported conditions */}
          {(data.conditions_supported || []).length > 0 && (
            <section>
              <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {bg ? "Подкрепя следните състояния" : "Supports conditions"}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {data.conditions_supported.map((cid) => (
                  <span key={cid} className={`text-[11px] px-2 py-0.5 rounded-md ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"}`}>
                    {data.condition_labels?.[cid]?.[bg ? "bg" : "en"] || cid}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* PMID refs */}
          {(data.pmid_refs || []).length > 0 && (
            <section>
              <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {bg ? "Научни референции" : "References"}
              </h3>
              <ul className="space-y-1">
                {data.pmid_refs.map((r, i) => (
                  <li key={i}>
                    <a href={`https://pubmed.ncbi.nlm.nih.gov/${r.pmid}/`} target="_blank" rel="noopener noreferrer"
                       className={`inline-flex items-center gap-1 text-[11px] underline hover:text-violet-500 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                      <ExternalLink className="w-3 h-3" /> {r.label} · PMID {r.pmid}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.safety_notes_bg && (
            <div className={`rounded-lg p-3 text-xs flex gap-2 ${isDark ? "bg-amber-500/10 text-amber-200 border border-amber-500/30" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{bg ? data.safety_notes_bg : (data.safety_notes_en || data.safety_notes_bg)}</span>
            </div>
          )}

          {/* Track in HI */}
          <div className="pt-2">
            <button
              onClick={() => onTrack?.(data.id)}
              disabled={tracking}
              data-testid="mindbody-track-btn"
              className="w-full px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {tracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {bg ? "Запиши в Health Intelligence" : "Save to Health Intelligence"}
            </button>
            <p className={`text-[10px] mt-1.5 text-center ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              {bg ? "Educational · не е медицински съвет" : "Educational · not medical advice"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function MindBodyLabPage() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const lang = language === "en" ? "en" : "bg";
  const bg = lang === "bg";

  const [disclaimerOpen, setDisclaimerOpen] = useState(() => {
    try { return localStorage.getItem(DISCLAIMER_KEY) !== "accepted_v1"; }
    catch { return true; }
  });

  const [catalog, setCatalog] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  // Profile state (persisted to localStorage)
  const STORAGE = "nutramilo:mindbody:profile:v1";
  const restored = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE) || "{}"); }
    catch { return {}; }
  }, []);
  const [conds,  setConds]  = useState(restored.conds  || []);
  const [symps,  setSymps]  = useState(restored.symps  || []);
  const [goals,  setGoals]  = useState(restored.goals  || []);
  const [cats,   setCats]   = useState(restored.cats   || []);

  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [synergy, setSynergy] = useState(null);
  const [condSearch, setCondSearch] = useState("");  // iter503 — search input for conditions

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE, JSON.stringify({ conds, symps, goals, cats }));
    } catch {}
  }, [conds, symps, goals, cats]);

  useEffect(() => {
    setLoadingCatalog(true);
    axios.get(`${API}/mindbody/catalog`).then((r) => {
      setCatalog(r.data);
    }).catch(() => {
      toast.error(bg ? "Грешка при зареждане на каталога" : "Failed to load catalog");
    }).finally(() => setLoadingCatalog(false));
  }, [API, bg]);

  // iter1017 — профилът захранва лабораторията: заболявания от „Моите данни"
  // и симптоми от дневника, ако потребителят още не е избирал сам.
  const [profilePrefill, setProfilePrefill] = useState(null);
  useEffect(() => {
    if (!catalog?.conditions?.length) return;
    axios.get(`${API}/profile-signals`, { params: { domain: "mindbody" } })
      .then(({ data }) => {
        const knownC = new Set(catalog.conditions.map((c) => c.id));
        const knownS = new Set((catalog.symptoms || []).map((x) => x.id));
        const cIds = (data.lab_condition_ids || []).filter((id) => knownC.has(id));
        const sIds = (data.symptom_ids || []).filter((id) => knownS.has(id));
        setProfilePrefill({ conds: cIds, symps: sIds });
        const untouched = !(restored.conds || []).length && !(restored.symps || []).length;
        if (untouched && (cIds.length || sIds.length)) {
          if (cIds.length) setConds((p) => (p.length ? p : cIds));
          if (sIds.length) setSymps((p) => (p.length ? p : sIds));
          toast.success("Взети са данните от профила Ви");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, catalog]);

  // Auto-recommend when profile is non-empty
  useEffect(() => {
    const empty = !conds.length && !symps.length && !goals.length;
    if (empty) {
      // still show ranked-by-evidence list
    }
    const ctrl = new AbortController();
    setSearching(true);
    axios.post(`${API}/mindbody/recommend`, {
      conditions: conds,
      symptoms:   symps,
      goals,
      categories: cats.length ? cats : null,
      limit: 20,
    }, { signal: ctrl.signal }).then((r) => {
      setResults(r.data);
    }).catch((e) => {
      if (axios.isCancel(e)) return;
      toast.error(bg ? "Грешка при ранжиране" : "Failed to rank");
    }).finally(() => setSearching(false));
    return () => ctrl.abort();
  }, [API, conds, symps, goals, cats, bg]);

  // Synergy fetch on mount
  useEffect(() => {
    axios.post(`${API}/mindbody/synergy/foods`, { conditions: conds, symptoms: symps, goals })
      .then((r) => setSynergy(r.data))
      .catch(() => {});
  }, [API, conds, symps, goals]);

  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/mindbody/intervention/${id}`);
      setDetailData(r.data);
    } catch {
      toast.error(bg ? "Грешка при зареждане" : "Failed to load");
    }
  };

  const trackIntervention = async (id) => {
    setTracking(true);
    try {
      await axios.post(`${API}/mindbody/track`, { intervention_id: id });
      toast.success(bg ? "Записано в Health Intelligence" : "Saved to Health Intelligence");
      setDetailData(null);
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (e.response?.status === 403) {
        toast.error(bg
          ? "Първо дайте съгласие в Health Intelligence"
          : "Please give consent in Health Intelligence first");
        navigate("/dashboard/health-intelligence");
      } else {
        toast.error(msg || (bg ? "Грешка при запис" : "Failed to save"));
      }
    } finally {
      setTracking(false);
    }
  };

  const txt   = isDark ? "text-slate-100" : "text-slate-900";
  const sub   = isDark ? "text-slate-300" : "text-slate-600";
  const muted = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(223,47%,6%)]" : "bg-slate-50"}`} data-testid="mindbody-lab-page">
      <DisclaimerModal open={disclaimerOpen}
        onAccept={() => setDisclaimerOpen(false)}
        onCancel={() => navigate("/dashboard")}
        lang={lang} isDark={isDark} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-pink-500 shadow-lg">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-2xl sm:text-3xl font-bold ${txt}`}>Mind & Body Lab</h1>
            <p className={`text-xs sm:text-sm mt-1 ${sub}`}>
              {bg
                ? "Evidence-based lifestyle medicine — движение, ум-тяло, сън, възстановяване, стрес."
                : "Evidence-based lifestyle medicine — movement, mind-body, sleep, recovery, stress."}
            </p>
            <button onClick={() => setDisclaimerOpen(true)} data-testid="mindbody-reopen-disclaimer"
                    className={`mt-1.5 inline-flex items-center gap-1 text-[10.5px] underline-offset-2 hover:underline ${isDark ? "text-amber-400/80" : "text-amber-700"}`}>
              <AlertTriangle className="w-3 h-3" />
              {bg ? "Educational tool — не замества медицински съвет" : "Educational tool — not medical advice"}
            </button>
          </div>
        </div>

        {loadingCatalog ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
          </div>
        ) : !catalog ? null : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LEFT — Profile builder */}
            <div className="lg:col-span-4 space-y-4">
              {/* iter1017 — какво знае профилът */}
              <ProfileContextStrip domain="mindbody" isDark={isDark} />
              {!!(profilePrefill?.conds?.length || profilePrefill?.symps?.length) && (
                <button
                  onClick={() => {
                    if (profilePrefill.conds.length) setConds(profilePrefill.conds);
                    if (profilePrefill.symps.length) setSymps(profilePrefill.symps);
                  }}
                  data-testid="mindbody-apply-profile"
                  className="w-full px-3 py-2 rounded-lg text-[11px] font-bold
                             bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25"
                >
                  Приложи от профила ({profilePrefill.conds.length} състояния
                  {profilePrefill.symps.length ? `, ${profilePrefill.symps.length} симптома` : ""})
                </button>
              )}
              {/* iter906 — Load from Pathway Vault (single source of truth) */}
              <MedModuleNotes module="mind" isDark={isDark} />
              <LoadFromVaultBanner
                currentConditions={conds}
                onApply={(ids) => setConds(ids)}
                isDark={isDark}
                lang={lang}
                testid="mindbody-load-from-vault"
              />
              {/* iter503 — How to start */}
              <HowToStartCard
                storageKey="nutramilo_howto_mindbody_lab"
                isDark={isDark}
                accent="from-violet-500 to-pink-500"
                testid="howto-mindbody-lab"
                title={bg ? "Как работи разделът „Ум и тяло“" : "How Mind & Body Lab works"}
                subtitle={bg
                  ? "Йога, кратки натоварвания, дихателни упражнения — подредени по силата на доказателствата за Вас."
                  : "Yoga, HIIT, meditation — ranked by evidence strength for you."}
                benefits={bg
                  ? ["Над 32 въздействия", "Оценка на съответствието", "Разговор с помощник", "Седмичен план"]
                  : ["32+ interventions", "IMS Match Score", "AI Lifestyle Coach", "Weekly plan"]}
                steps={bg ? [
                  { title: "1. Изберете състояния, оплаквания и цели",
                    body: "Кратък профил: едно до три състояния (поликистозни яйчници, инсулинова резистентност…) и до пет оплаквания (умора, лош сън)." },
                  { title: "2. Вижте подредените въздействия",
                    body: "Всяко въздействие получава оценка за съответствие от 0 до 100 — колко добре приляга на профила Ви." },
                  { title: "3. Прибавете го в седмичния си план",
                    body: "Плъзгате го върху желания ден, а съчетанието с Хранителната лаборатория се преценява само." },
                  { title: "4. Разговор с помощника",
                    body: "Питайте защо точно това въздействие, как да започнете леко и какво да очаквате за две седмици." },
                ] : [
                  { title: "1. Pick conditions, symptoms & goals",
                    body: "Quick profile: 1-3 conditions (PCOS, insulin resistance…) + up to 5 symptoms (fatigue, poor sleep)." },
                  { title: "2. See ranked interventions",
                    body: "Each gets an IMS Match Score (0-100) — how well it fits your profile." },
                  { title: "3. Add to your weekly plan",
                    body: "Drag-and-drop into days + automatic Synergy Score with Nutrition Lab." },
                  { title: "4. Chat with the AI Lifestyle Coach",
                    body: "Ask why this intervention, how to start gently, what to expect in 2 weeks." },
                ]}
              />

              {/* Categories filter */}
              <section data-testid="mindbody-categories" className={`rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Filter className={`w-4 h-4 ${muted}`} />
                  <h2 className={`text-xs font-bold uppercase tracking-wider ${txt}`}>
                    {bg ? "Категории" : "Categories"}
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(CATEGORY_LABELS[lang]).map((cid) => {
                    const Icon = CATEGORY_ICONS[cid];
                    const active = cats.includes(cid);
                    return (
                      <button key={cid} onClick={() => toggle(cats, setCats, cid)}
                              data-testid={`mindbody-cat-${cid}`}
                              className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12px] font-medium border transition ${
                                active
                                  ? `bg-gradient-to-r ${CATEGORY_COLORS[cid]} text-white border-transparent shadow`
                                  : (isDark ? "bg-slate-800/50 text-slate-300 border-slate-700 hover:border-violet-500/50" : "bg-slate-50 text-slate-700 border-slate-200 hover:border-violet-500/50")
                              }`}>
                        <Icon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{CATEGORY_LABELS[lang][cid]}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Conditions — iter503: search + scroll */}
              <ProfileSection
                title={bg ? `Състояния (${conds.length}${catalog.conditions.length ? ` / ${catalog.conditions.length}` : ""})` : `Conditions (${conds.length}${catalog.conditions.length ? ` / ${catalog.conditions.length}` : ""})`}
                testid="mindbody-conditions"
                isDark={isDark} muted={muted} txt={txt}
              >
                {/* Search input */}
                <div className="relative mb-2">
                  <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${muted}`} />
                  <input
                    type="text"
                    value={condSearch}
                    onChange={(e) => setCondSearch(e.target.value)}
                    placeholder={bg ? "Търси състояние…" : "Search a condition…"}
                    data-testid="mindbody-cond-search"
                    className={`w-full pl-8 pr-7 py-1.5 text-[11px] rounded-lg border outline-none transition ${
                      isDark
                        ? "bg-slate-950 border-slate-700 text-slate-200 focus:border-violet-500"
                        : "bg-white border-slate-200 text-slate-800 focus:border-violet-500"
                    }`}
                  />
                  {condSearch && (
                    <button
                      onClick={() => setCondSearch("")}
                      data-testid="mindbody-cond-search-clear"
                      className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full flex items-center justify-center ${
                        isDark ? "bg-slate-700 hover:bg-slate-600 text-white" : "bg-slate-200 hover:bg-slate-300 text-slate-700"
                      }`}
                      aria-label="Clear"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </div>

                {/* Selected pills */}
                {conds.length > 0 && (
                  <div
                    className={`mb-2 p-1.5 rounded-lg flex flex-wrap gap-1 ${
                      isDark ? "bg-violet-500/5 border border-violet-500/20" : "bg-violet-50 border border-violet-200"
                    }`}
                    data-testid="mindbody-selected-conds"
                  >
                    {conds.map((cid) => {
                      const c = catalog.conditions.find((x) => x.id === cid);
                      if (!c) return null;
                      return (
                        <button
                          key={cid}
                          onClick={() => toggle(conds, setConds, cid)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-600 text-white hover:bg-violet-700 transition"
                        >
                          {bg ? c.name_bg : c.name_en}
                          <X className="w-2.5 h-2.5" />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Scrollable list */}
                <div className="flex flex-wrap gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="mindbody-conditions-scroll">
                  {(() => {
                    const q = condSearch.trim().toLowerCase();
                    const filtered = catalog.conditions.filter((c) => {
                      if (!q) return true;
                      return (
                        (c.name_bg || "").toLowerCase().includes(q) ||
                        (c.name_en || "").toLowerCase().includes(q) ||
                        (c.id || "").toLowerCase().includes(q)
                      );
                    });
                    if (filtered.length === 0) {
                      return (
                        <div className={`w-full text-center py-3 text-[11px] ${muted}`}>
                          {bg ? "Няма съвпадения." : "No matches."}
                        </div>
                      );
                    }
                    return filtered.map((c) => {
                      const active = conds.includes(c.id);
                      return (
                        <button key={c.id} onClick={() => toggle(conds, setConds, c.id)}
                                data-testid={`mindbody-cond-${c.id}`}
                                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                                  active ? "bg-violet-600 text-white border-violet-600" :
                                  (isDark ? "bg-slate-800 text-slate-300 border-slate-700 hover:border-violet-500/50" : "bg-white text-slate-700 border-slate-200 hover:border-violet-500/50")
                                }`}>
                          {bg ? c.name_bg : c.name_en}
                        </button>
                      );
                    });
                  })()}
                </div>
              </ProfileSection>

              {/* Symptoms */}
              <ProfileSection title={bg ? "Симптоми" : "Symptoms"} testid="mindbody-symptoms" isDark={isDark} muted={muted} txt={txt}>
                <div className="flex flex-wrap gap-1.5">
                  {catalog.symptoms.map((s) => {
                    const active = symps.includes(s.id);
                    return (
                      <button key={s.id} onClick={() => toggle(symps, setSymps, s.id)}
                              data-testid={`mindbody-symp-${s.id}`}
                              className={`text-[11px] px-2.5 py-1 rounded-full border ${
                                active ? "bg-cyan-600 text-white border-cyan-600" :
                                (isDark ? "bg-slate-800 text-slate-300 border-slate-700 hover:border-cyan-500/50" : "bg-white text-slate-700 border-slate-200 hover:border-cyan-500/50")
                              }`}>
                        {bg ? s.name_bg : s.name_en}
                      </button>
                    );
                  })}
                </div>
              </ProfileSection>

              {/* Goals */}
              <ProfileSection title={bg ? "Цели" : "Goals"} testid="mindbody-goals" isDark={isDark} muted={muted} txt={txt}>
                <div className="flex flex-wrap gap-1.5">
                  {catalog.goals.map((g) => {
                    const active = goals.includes(g.id);
                    return (
                      <button key={g.id} onClick={() => toggle(goals, setGoals, g.id)}
                              data-testid={`mindbody-goal-${g.id}`}
                              className={`text-[11px] px-2.5 py-1 rounded-full border ${
                                active ? "bg-pink-600 text-white border-pink-600" :
                                (isDark ? "bg-slate-800 text-slate-300 border-slate-700 hover:border-pink-500/50" : "bg-white text-slate-700 border-slate-200 hover:border-pink-500/50")
                              }`}>
                        {bg ? g.name_bg : g.name_en}
                      </button>
                    );
                  })}
                </div>
              </ProfileSection>

              {/* Дишане — същият таймер и същият дневник като в кортизоловия модул */}
            <section data-testid="mindbody-breath">
              <CortisolBreathTimer isDark={isDark} />
            </section>

            {/* Synergy with Nutrition Lab */}
              {synergy && synergy.count > 0 && (
                <section data-testid="mindbody-synergy" className={`rounded-2xl border p-4 bg-gradient-to-br ${isDark ? "from-emerald-900/30 to-cyan-900/30 border-emerald-700/40" : "from-emerald-50 to-cyan-50 border-emerald-200"}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Salad className={`w-4 h-4 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
                    <h2 className={`text-xs font-bold uppercase tracking-wider ${txt}`}>
                      {bg ? "Synergy с Nutrition Lab" : "Synergy with Nutrition Lab"}
                    </h2>
                  </div>
                  <p className={`text-[11px] mb-2 ${sub}`}>
                    {bg ? synergy.tip_bg : synergy.tip_en}
                  </p>
                  <div className="space-y-1.5">
                    {synergy.pinned_foods.slice(0, 4).map((f, i) => (
                      <div key={i} className={`text-[11px] flex items-center justify-between gap-2 px-2 py-1 rounded ${isDark ? "bg-slate-800/50" : "bg-white"}`}>
                        <span className={`truncate ${txt}`}>{bg ? (f.name_bg || f.name_en) : (f.name_en || f.name_bg)}</span>
                        <span className={`text-[10px] font-bold ${isDark ? "text-emerald-300" : "text-emerald-600"}`}>{(f.fms ?? 0).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => navigate("/dashboard/nutrition-lab")} data-testid="mindbody-open-foodlab"
                          className={`mt-2 w-full text-[11px] font-bold px-2 py-1.5 rounded-lg flex items-center justify-center gap-1 ${isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
                    {bg ? "Към Nutrition Lab" : "Open Nutrition Lab"} <ChevronRight className="w-3 h-3" />
                  </button>
                </section>
              )}
            </div>

            {/* RIGHT — Results */}
            <div className="lg:col-span-8 space-y-4">
              {searching && (
                <div className="flex items-center gap-2 py-3 px-4 rounded-xl bg-violet-500/10 text-violet-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-xs">{bg ? "Ранжирам интервенции..." : "Ranking interventions..."}</span>
                </div>
              )}

              {/* Phenotypes pill bar */}
              {results?.resolved_phenotypes?.length > 0 && (
                <div className={`text-[11px] flex flex-wrap items-center gap-1.5 ${muted}`} data-testid="mindbody-phenotypes">
                  <span className={`font-bold ${txt}`}>{bg ? "Активни фенотипи:" : "Active phenotypes:"}</span>
                  {results.resolved_phenotypes.slice(0, 6).map((p) => (
                    <span key={p} className={`px-2 py-0.5 rounded ${isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-700"}`}>{p}</span>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mindbody-results">
                {(results?.interventions || []).map((it) => {
                  const Icon = CATEGORY_ICONS[it.category];
                  return (
                    <button key={it.id} onClick={() => openDetail(it.id)}
                            data-testid={`mindbody-result-${it.id}`}
                            className={`text-left rounded-2xl border p-4 transition hover:border-violet-500/50 ${isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"}`}>
                      <div className="flex items-start gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${CATEGORY_COLORS[it.category]} shrink-0`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-bold ${txt} truncate`}>{bg ? it.name_bg : it.name_en}</div>
                          <div className={`text-[10px] mt-0.5 ${muted}`}>{CATEGORY_LABELS[lang][it.category]}</div>
                        </div>
                        <div className={`px-2 py-1 rounded-lg bg-gradient-to-r ${imsColor(it.ims)} text-white text-center shrink-0`} title="Intervention Match Score">
                          <div className="text-[8px] font-bold leading-none uppercase tracking-wider">IMS</div>
                          <div className="text-base font-black leading-tight">{it.ims.toFixed(0)}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${EVIDENCE_LABEL[it.evidence_level].color} text-white`}>
                          {it.evidence_level}
                        </span>
                        {it.conditions_matched.slice(0, 2).map((c) => (
                          <span key={c} className={`text-[9.5px] px-1.5 py-0.5 rounded ${isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
                            ✓ {c}
                          </span>
                        ))}
                        {it.goals_matched.slice(0, 1).map((g) => (
                          <span key={g} className={`text-[9.5px] px-1.5 py-0.5 rounded ${isDark ? "bg-pink-500/20 text-pink-300" : "bg-pink-100 text-pink-700"}`}>
                            🎯 {g}
                          </span>
                        ))}
                      </div>
                      <div className={`text-[11px] ${sub} line-clamp-2`}>
                        {bg ? it.dose_bg : it.dose_en}
                      </div>
                    </button>
                  );
                })}
              </div>

              {results && results.interventions.length === 0 && !searching && (
                <div className={`text-center py-12 ${muted}`}>
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">{bg ? "Няма интервенции — пробвай по-малко филтри." : "No interventions — try fewer filters."}</p>
                </div>
              )}

              {results?.disclaimer_bg && (
                <p className={`text-[10px] text-center pt-2 ${muted}`}>{results.disclaimer_bg}</p>
              )}

              {/* iter487 — Phase 2: Synergy Score (Food + Lifestyle) */}
              <SynergyScoreCard
                API={API}
                profile={{ conds, symps, goals }}
                lang={lang}
                isDark={isDark}
                onOpenFoodlab={() => navigate("/dashboard/nutrition-lab")}
              />

              {/* iter487 — Phase 2: Personal Weekly Plan (rule-based + GPT-5.2 polish) */}
              <WeeklyPlanCard
                API={API}
                profile={{ conds, symps, goals }}
                lang={lang}
                isDark={isDark}
              />

              {/* iter487 — Phase 3: AI Lifestyle Coach + Correlations */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <AiCoachChat
                  API={API}
                  profile={{ conds, symps, goals }}
                  lang={lang}
                  isDark={isDark}
                />
                <CorrelationsCard
                  API={API}
                  lang={lang}
                  isDark={isDark}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <InterventionDetailModal data={detailData} lang={lang} isDark={isDark}
        onClose={() => setDetailData(null)}
        onTrack={trackIntervention} tracking={tracking} />
    </div>
  );
}

function ProfileSection({ title, testid, isDark, muted, txt, children }) {
  return (
    <section data-testid={testid} className={`rounded-2xl border p-4 ${isDark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`}>
      <h2 className={`text-xs font-bold uppercase tracking-wider ${txt} mb-2`}>{title}</h2>
      {children}
    </section>
  );
}
